/*
  File: src/components/Stream/DetectionsBar.jsx
  Purpose: Bottom-aligned detections bar that shows live detections as cards.
  Behavior:
  - Renders horizontally scrollable cards from `history` (latest first).
  - On card click, opens product grid (up to 4) like BottomMetadataOverlay.
  - On product click, opens product detail with variant selectors and Add to Cart.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useStreaming } from '../../contexts/StreamingContext';
import DetectionsConfig from '../../config/detections';
import { getCachedPlayers, setCachedPlayers } from '../../utils/playerCache';
import AddToDataset from '../common/AddToDataset';

// Removed product grid logic; this bar now displays raw IVS metadata as formatted JSON

function extractCombos(meta) {
    const out = [];
    if (!meta || typeof meta !== 'object') return out;
    const arr = Array.isArray(meta.teams) ? meta.teams : [];
    arr.forEach((t) => {
        const team = t?.t || t?.team || '';
        const inst = Array.isArray(t?.i) ? t.i : (Array.isArray(t?.instances) ? t.instances : []);
        if (inst.length > 0) {
            inst.forEach((x) => {
                let num;
                const n = x?.n;
                if (n && typeof n === 'object' && !Array.isArray(n)) num = n.t ?? n.value;
                else if (Array.isArray(n) && n.length > 0) {
                    const first = n[0];
                    num = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                }
                out.push({ team, variant: x?.v, side: x?.s, player: x?.p || x?.player, number: num, jc: x?.cj, ns: (n && n.c) ? n.c : undefined });
            });
        } else {
            let num;
            const n = t?.n;
            if (Array.isArray(n) && n.length > 0) {
                const first = n[0];
                num = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
            } else if (typeof n === 'number' || typeof n === 'string') num = n;
            out.push({ team, player: t?.p || t?.player, number: num });
        }
    });
    return out;
}

export default function DetectionsBar({ history }) {
    const { streamSessionId } = useStreaming();
    const latest = useMemo(() => (Array.isArray(history) ? history : []), [history]);
    const DEDUP_ENABLED = !!DetectionsConfig.ignoreRepeatEnabled;
    const DEDUP_WINDOW_MS = Math.max(0, (DetectionsConfig.ignoreRepeatSeconds ?? 3) * 1000);
    const [enriched, setEnriched] = useState({}); // { [id]: [{image_url, display_name, display_team, number}] }
    const [modalOpen, setModalOpen] = useState(false);
    const [modalItem, setModalItem] = useState(null);
    const [modalUrl, setModalUrl] = useState('');

    const rawBase = (process.env.REACT_APP_API_BASE || '').trim();
    const cleanedBase = rawBase.replace(/^@+/, '');
    const API_BASE = cleanedBase || '/dev';

    const openFrameModal = async (item) => {
        setModalItem(item);
        setModalUrl('');
        setModalOpen(true);
        try {
            const img = item?.img;
            let url; 
            if (img && typeof img === 'object' && img.b && img.k) {
                const res = await fetch(`${API_BASE}/media/sign-url`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json', accept: 'application/json' },
                    body: JSON.stringify({ bucket: img.b, key: img.k, expires_in_seconds: 3600 })
                });
                if (res.ok) {
                    const data = await res.json();
                    url = data?.url;
                }
            }
            if (!url) url = findFrameUrl(item);
            if (url) setModalUrl(url);
        } catch (_) {
            const url = findFrameUrl(item);
            if (url) setModalUrl(url);
        }
    };

    const closeModal = () => { setModalOpen(false); setModalItem(null); setModalUrl(''); };

    // Within the last 10 seconds, avoid showing repeated items with the same
    // (player|number|team) combos. Iterate newest->older and keep only items
    // that introduce at least one new combo not seen within the window.
    const latestDeduped = useMemo(() => {
        const out = [];
        const lastSeen = new Map(); // playerKey -> timestamp
        const now = Date.now();
        for (const item of latest) {
            const ts = item?.tim?.shown_ms || item?.tim?.shown || item?.tim?.clt_recv_ms || item?.tim?.sent || now;
            // Prefer deduping by enriched player names if available; otherwise fallback to raw metadata players
            let playerKeys = [];
            const enrichedCombos = enriched[item?.id];
            if (Array.isArray(enrichedCombos) && enrichedCombos.length > 0) {
                playerKeys = Array.from(new Set(enrichedCombos.map(e => String(e.display_name || '').toLowerCase()).filter(Boolean)));
            } else {
                const combos = extractCombos(item);
                playerKeys = Array.from(new Set(combos.map(c => String(c.player || '').toLowerCase()).filter(Boolean)));
            }
            // If we couldn't derive any player identity, do not block the item by time window (player-only rule)
            if (playerKeys.length === 0) {
                out.push(item);
                continue;
            }
            if (!DEDUP_ENABLED) {
                out.push(item);
                continue;
            }
            let fresh = false;
            for (const pk of playerKeys) {
                const last = lastSeen.get(pk);
                if (!last || ts - last >= DEDUP_WINDOW_MS) {
                    fresh = true;
                }
            }
            if (fresh) {
                out.push(item);
                for (const pk of playerKeys) lastSeen.set(pk, ts);
            }
        }
        return out;
    }, [latest, enriched]);

    const enrichCombosBatch = async (filters) => {
        const base = process.env.REACT_APP_API_BASE || '/dev';
        try {
            const t0 = Date.now();
            const queries = filters.map((f) => {
                const teamRaw = f.team ? String(f.team).toLowerCase() : '';
                // For face_only, search only by player name and omit team entirely
                if (teamRaw === 'face_only') {
                    const names = f.player ? [String(f.player)] : [];
                    return { names };
                }
                const tokens = [];
                if (typeof f.number !== 'undefined' && f.number !== null && String(f.number) !== '') tokens.push(String(f.number));
                if (f.player) tokens.push(String(f.player));
                return { team: teamRaw || null, names: tokens };
            });
            if (!DetectionsConfig.playerCacheEnabled) {
                const res = await fetch(`${base}/players/search`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json', accept: 'application/json' },
                    body: JSON.stringify({ queries })
                });
                if (!res.ok) return filters.map(() => null);
                const data = await res.json();
                const items = Array.isArray(data?.items) ? data.items : [];
                return filters.map((f, idx) => {
                    const first = items[idx];
                    if (!first) return null;
                    return {
                        image_url: first.image_url || undefined,
                        display_name: first.display_name || (Array.isArray(first.names) ? first.names[0] : undefined) || f.player,
                        display_team: first.team || f.team,
                        number: f.number
                    };
                });
            }
            // Cache lookup
            const cached = getCachedPlayers(queries);
            const result = new Array(filters.length).fill(null);
            const need = [];
            const needIdx = [];
            cached.forEach((c, i) => {
                if (c && (c.id || c.display_name || c.image_url)) {
                    result[i] = {
                        image_url: c.image_url || undefined,
                        display_name: c.display_name || (Array.isArray(c.names) ? c.names[0] : undefined) || filters[i].player,
                        display_team: c.team || filters[i].team,
                        number: filters[i].number
                    };
                } else {
                    need.push(queries[i]);
                    needIdx.push(i);
                }
            });
            const res = await fetch(`${base}/players/search`, {
                method: 'POST',
                headers: { 'content-type': 'application/json', accept: 'application/json' },
                body: JSON.stringify({ queries: need.length ? need : [] })
            });
            if (!res.ok) return result;
            const data = await res.json();
            const t1 = Date.now();
            const items = Array.isArray(data?.items) ? data.items : [];
            setCachedPlayers(need, items);
            items.forEach((first, j) => {
                const idx = needIdx[j];
                if (!first) return;
                result[idx] = {
                    image_url: first.image_url || undefined,
                    display_name: first.display_name || (Array.isArray(first.names) ? first.names[0] : undefined) || filters[idx].player,
                    display_team: first.team || filters[idx].team,
                    number: filters[idx].number
                };
            });
            return result;
        } catch (_) {
            if (!DetectionsConfig.playerCacheEnabled) return filters.map(() => null);
            const queriesFallback = filters.map((f) => ({ team: f.team ? String(f.team).toLowerCase() : null, names: [f.number, f.player].filter(Boolean) }));
            const cached = getCachedPlayers(queriesFallback);
            return cached.map((c, i) => c ? ({
                image_url: c.image_url || undefined,
                display_name: c.display_name || (Array.isArray(c.names) ? c.names[0] : undefined) || filters[i].player,
                display_team: c.team || filters[i].team,
                number: filters[i].number
            }) : null);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const targets = latestDeduped.filter((item) => item && item.id && !enriched[item.id]);
            if (targets.length === 0) return;
            const tasks = targets.map(async (item) => {
                const combos = extractCombos(item);
                const enrichedCombos = (await enrichCombosBatch(combos)).filter(Boolean);
                return { id: item.id, enrichedCombos };
            });
            const results = await Promise.all(tasks);
            if (cancelled) return;
            const updates = {};
            for (const r of results) updates[r.id] = r.enrichedCombos;
            if (Object.keys(updates).length > 0) setEnriched(prev => ({ ...prev, ...updates }));
        })();
        return () => { cancelled = true; };
    }, [latestDeduped, enriched]);

    return (
        <div className="w-full">
            <div className="mt-2">
                <div className="py-2 brand-muted text-sm">Live Detections</div>
                {/* Vertical list aligned with the video feed */}
                <div className="relative max-h-[70vh] overflow-y-auto no-scrollbar pr-1">
                    <div className="flex flex-col gap-3 pb-2">
                        {latestDeduped.length === 0 && (
                            <div className="brand-muted text-sm">No detections yet</div>
                        )}
                        {latestDeduped.map((item) => {
                            const combos = enriched[item.id] || [];
                            if (combos.length === 0) return null; // hide if no player match found
                            return (
                                <div
                                    key={item.id}
                                    className="w-full rounded-xl border border-[#4cc3e9]/20 bg-gradient-to-br from-[#1a2448]/60 to-[#1a2448]/40 p-3 hover:border-[#4cc3e9]/40 hover:from-[#1a2448]/70 hover:to-[#1a2448]/50 transition-colors"
                                >
                                    <div 
                                        className="cursor-pointer"
                                        onClick={() => openFrameModal(item)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openFrameModal(item); }}
                                    >
                                        <div className="text-[11px] text-white/70">{new Date(item?.tim?.shown || item?.tim?.sent || Date.now()).toLocaleTimeString()}</div>
                                        <div className="mt-1 space-y-1">
                                            {combos.map((c, idx) => (
                                                <div key={idx} className="flex items-center justify-between text-[12px]">
                                                    <div className="flex items-center gap-2">
                                                        {c.image_url ? (
                                                            <img
                                                                src={c.image_url}
                                                                alt={c.display_name || 'player'}
                                                                className="w-6 h-6 rounded-full object-cover border border-white/20"
                                                                loading="lazy"
                                                                decoding="async"
                                                            />
                                                        ) : (
                                                            <div className="w-5 h-5 rounded-full bg-white/10 border border-white/20" />
                                                        )}
                                                        <span className="capitalize font-semibold">
                                                            {c.display_name || ''}{c.number ? ` ${c.number}` : ''}{(c.display_name || c.number) && c.display_team ? ` (${c.display_team})` : (c.display_team || '')}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-[10px] text-white/60">
                                                        {typeof c.jc === 'number' ? <span>jersey {c.jc.toFixed(1)}%</span> : null}
                                                        {typeof c.ns === 'number' ? <span># {c.ns.toFixed(1)}%</span> : null}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-white/10 flex justify-end">
                                        <AddToDataset
                                            detectionId={item.id}
                                            mode="button"
                                            compact={true}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            {modalOpen && (
                <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={closeModal}>
                    <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={closeModal} className="absolute -top-12 right-0 px-4 py-2 rounded-lg bg-gradient-to-r from-[#7b3ff2] to-[#9060ff] text-white text-sm font-medium border border-[#9060ff]/50">✕ Close</button>
                        <div className="space-y-4">
                            <div className="relative w-full aspect-[16/9] bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl overflow-hidden border border-[#4cc3e9]/30">
                                {modalUrl ? (
                                    <img src={modalUrl} alt="Detection" className="w-full h-full object-cover" loading="eager" />
                                ) : (
                                    <div className="flex items-center justify-center h-full text-[#4cc3e9]/60">Loading image…</div>
                                )}
                                <TeamBoxes item={modalItem} />
                            </div>
                            <div className="bg-gradient-to-br from-[#1a2448]/80 to-[#1a2448]/60 rounded-xl p-4 border border-[#4cc3e9]/30">
                                <AddToDataset
                                    detectionId={modalItem?.id}
                                    mode="full"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Modal overlay rendering the frame with boxes and detected players
function TeamBoxes({ item }) {
    if (!item) return null;
    const arr = Array.isArray(item.teams) ? item.teams : [];
    const teams = [];
    arr.forEach((t) => {
        const teamLabel = t?.t || t?.team || '';
        const instList = Array.isArray(t?.i) ? t.i : (Array.isArray(t?.instances) ? t.instances : []);
        if (instList.length > 0) {
            instList.forEach((inst) => {
                const jerseyConf = typeof inst?.cj === 'number' ? inst.cj : (typeof inst?.jersey?.confidence === 'number' ? inst.jersey.confidence : undefined);
                const jerseyBox = Array.isArray(inst?.bj) && inst.bj.length === 4
                    ? inst.bj
                    : (Array.isArray(inst?.jersey?.box_rel) && inst.jersey.box_rel.length === 4 ? inst.jersey.box_rel : undefined);
                const playerName = inst?.p || inst?.player || '';
                const playerSim = typeof inst?.sp === 'number' ? inst.sp : (typeof inst?.player?.similarity === 'number' ? inst.player.similarity : undefined);
                const playerBox = Array.isArray(inst?.bp) && inst.bp.length === 4
                    ? inst.bp
                    : (Array.isArray(inst?.player?.box_rel) && inst.player.box_rel.length === 4 ? inst.player.box_rel : undefined);
                let numbers = [];
                const n = inst?.n;
                if (n && typeof n === 'object' && !Array.isArray(n)) {
                    const text = n.t ?? n.text ?? n.value ?? '';
                    const conf = n.c ?? n.confidence;
                    const bbox = Array.isArray(n.b) && n.b.length === 4 ? n.b : undefined;
                    numbers = [{ t: String(text || ''), c: conf, b: bbox }];
                } else if (Array.isArray(n)) {
                    numbers = n.map((v) => {
                        if (typeof v === 'string' || typeof v === 'number') return { t: String(v), c: undefined, b: undefined };
                        if (v && typeof v === 'object') {
                            const text = v.t ?? v.text ?? v.value ?? '';
                            const conf = v.c ?? v.confidence;
                            const bbox = Array.isArray(v.b) && v.b.length === 4 ? v.b : undefined;
                            return { t: String(text || ''), c: conf, b: bbox };
                        }
                        return { t: '', c: undefined, b: undefined };
                    });
                }
                teams.push({ teamLabel, jerseyConf, jerseyBox, playerName, playerSim, playerBox, numbers });
            });
        }
    });

    const Box = ({ box, color = 'border-yellow-400', label, withLabel = true, labelBgClass, labelTextClass = 'text-black', labelOffsetPx = 0 }) => {
        if (!box || box.length !== 4) return null;
        const [L, T, W, H] = box;
        const left = Math.max(0, Math.min(100, L * 100));
        const top = Math.max(0, Math.min(100, T * 100));
        const width = Math.max(0, Math.min(100, W * 100));
        const height = Math.max(0, Math.min(100, H * 100));
        const placeRight = left + width < 80;
        const bgClass = labelBgClass || color.replace('border-', 'bg-');
        return (
            <div className={`absolute ${color}`} style={{ left: `${left}%`, top: `${top}%`, width: `${width}%`, height: `${height}%` }}>
                {withLabel && (
                    <div className={`absolute ${placeRight ? 'left-full ml-2' : 'right-full mr-2'} px-1.5 py-0.5 rounded text-[10px] font-semibold ${labelTextClass} ${bgClass} shadow`} style={{ top: `${-labelOffsetPx}px` }}>{label}</div>
                )}
                <div className={`w-full h-full border-2 ${color}`} />
            </div>
        );
    };

    return (
        <div className="absolute inset-0 pointer-events-none">
            {teams.map((t, idx) => (
                <React.Fragment key={idx}>
                    <Box box={t.jerseyBox} color="border-yellow-400" label={`${t.teamLabel || 'team'}${typeof t.jerseyConf === 'number' ? ` ${t.jerseyConf.toFixed(1)}%` : ''}`} withLabel={true} />
                    <Box box={t.playerBox} color="border-cyan-400" label={`${t.playerName || 'player'}${typeof t.playerSim === 'number' ? ` ${t.playerSim.toFixed(1)}%` : ''}`} withLabel={true} labelBgClass="bg-blue-600" labelTextClass="text-white" labelOffsetPx={14} />
                    {Array.isArray(t.numbers) && t.numbers.map((n, i) => (
                        <Box key={i} box={n.b} color="border-fuchsia-400" label={`${n.t ? `#${n.t}` : 'number'}${typeof n.c === 'number' ? ` ${n.c.toFixed(1)}%` : ''}`} withLabel={true} />
                    ))}
                </React.Fragment>
            ))}
        </div>
    );
}

// Attempt to find a frame image URL (e.g., S3 URL with boxes drawn) within a detection item
function findFrameUrl(item) {
    if (!item || typeof item !== 'object') return undefined;
    const candidates = [item.img, item.frame_url, item.frameUrl, item.s3_url, item.s3Url, item.image_url, item.image, item.url].filter(Boolean);
    if (candidates.length > 0) return String(candidates[0]);
    if (item.tim && typeof item.tim === 'object') {
        const nested = [item.tim.img, item.tim.url].filter(Boolean);
        if (nested.length > 0) return String(nested[0]);
    }
    if (Array.isArray(item.frames) && item.frames.length > 0) {
        const f = item.frames[0];
        if (typeof f === 'string') return f;
        if (f && typeof f === 'object') {
            const u = f.url || f.img || f.image_url || f.image;
            if (u) return String(u);
        }
    }
    return undefined;
}
